import React from "react";
import { TitleProps } from "@mantine/core";
import cx from "clsx";
import styles from "@scss/custom-components/custom-split-title.module.scss";
import { motion, type Variants } from "framer-motion"

interface CustomSplitTitleProps extends Omit<TitleProps, 'children'> {
  partA: string;
  partB: string;
  partC?: string;
  partAStyle?: React.CSSProperties;
  partBStyle?: React.CSSProperties;
  partCStyle?: React.CSSProperties;
  partAClassName?: string;
  partBClassName?: string;
  partCClassName?: string;
  uppercase?: boolean;
  alignCenter?: boolean;
  textAlign?: 'left' | 'center' | 'right';
  useMaskAnimation?: boolean;
  animationDelay?: number;
  animationDuration?: number;
  removeBreaksOnDesktop?: boolean;
  mainTitle?: boolean;
}

const parseTextWithBreaks = (text: string, removeBreaksOnDesktop = false) => {
  const parts = text.split('<br/>');
  
  if (removeBreaksOnDesktop) {
    return (
      <>
        <span className="mobile-only">
          {parts.map((part, index) => (
            <React.Fragment key={index}>
              {part}
              {index < parts.length - 1 && <br />}
            </React.Fragment>
          ))}
        </span>
        <span className="desktop-only">
          {parts.join(' ')}
        </span>
      </>
    );
  }
  
  return parts.map((part, index) => (
    <React.Fragment key={index}>
      {part}
      {index < parts.length - 1 && <br />}
    </React.Fragment>
  ));
};

const parseTextToWords = (text: string) => {
  // Split by <br/> to preserve line breaks, then split each line into words
  const lines = text.split('<br/>');
  return lines.map(line => ({
    words: line.trim().split(' ').filter(word => word.length > 0),
    isLineBreak: false
  }));
};

export const CustomSplitTitle: React.FC<CustomSplitTitleProps> = ({
  partA,
  partB,
  partC,
  partAStyle,
  partBStyle,
  partCStyle,
  partAClassName,
  partBClassName,
  partCClassName,
  uppercase = false,
  alignCenter = false,
  textAlign,
  useMaskAnimation = false,
  animationDelay = 0,
  animationDuration = 0.8,
  removeBreaksOnDesktop = false,
  order = 1,
  mainTitle = false,
  ...titleProps
}) => {
  const partOneIsOver25Characters = partA.replace(/<br\/>/g, '').length > 25;
  const headingClass = order === 2 ? styles.h2 : styles.h1;
  const additionalClass = mainTitle && partOneIsOver25Characters ? styles.longHeading : '';

  const containerVariants: Variants = {
    hidden: {},
    visible: {
      transition: {
        delayChildren: animationDelay,
        staggerChildren: 0.05,
      },
    },
  };

  const wordVariants: Variants = {
    hidden: {
      y: "100%",
    },
    visible: {
      y: 0,
      transition: {
        duration: animationDuration,
        ease: [0.25, 0.1, 0.25, 1],
      },
    },
  };

  // Mask animation rendering
  if (useMaskAnimation) {
    const partALines = parseTextToWords(partA);
    const partBLines = parseTextToWords(partB);
    const partCLines = partC ? parseTextToWords(partC) : null;

    return (
      <div className="custom-split-title overflow-hidden" style={{ zIndex: 9, margin: alignCenter ? '0 auto' : '0' }}>
        <motion.div
          className={cx(styles.splitTitle, headingClass, additionalClass, titleProps.className)}
          style={{ 
            textTransform: uppercase ? 'uppercase' : 'none',
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: textAlign === 'center' ? 'center' : textAlign === 'right' ? 'flex-end' : 'flex-start',
            lineHeight: '93.6%',
            letterSpacing: '-2px',
            gap: 0,
            columnGap: '0.15em',
            ...(textAlign && { textAlign })
          }}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {partALines.map((line, lineIndex) => (
            <React.Fragment key={`a-line-${lineIndex}`}>
              {line.words.map((word, wordIndex) => (
                <span key={`a-${lineIndex}-${wordIndex}`} style={{ overflow: 'hidden', display: 'inline-block', paddingRight: '0.1em' }}>
                  <motion.span 
                    className={cx(styles.partA, partAClassName)} 
                    style={{ display: 'inline-block', ...partAStyle }}
                    variants={wordVariants}
                  >
                    {word}
                  </motion.span>
                </span>
              ))}
            </React.Fragment>
          ))}
          {partBLines.map((line, lineIndex) => (
            <React.Fragment key={`b-line-${lineIndex}`}>
              {line.words.map((word, wordIndex) => (
                <span key={`b-${lineIndex}-${wordIndex}`} style={{ overflow: 'hidden', display: 'inline-block', paddingRight: '0.1em' }}>
                  <motion.span 
                    className={cx(styles.partB, partBClassName)} 
                    style={{ display: 'inline-block', ...partBStyle }}
                    variants={wordVariants}
                  >
                    {word}
                  </motion.span>
                </span>
              ))}
            </React.Fragment>
          ))}
          {partCLines && partCLines.map((line, lineIndex) => (
            <React.Fragment key={`c-line-${lineIndex}`}>
              {line.words.map((word, wordIndex) => (
                <span key={`c-${lineIndex}-${wordIndex}`} style={{ overflow: 'hidden', display: 'inline-block', paddingRight: '0.1em' }}>
                  <motion.span 
                    className={cx(styles.partC, partCClassName)} 
                    style={{ display: 'inline-block', ...partCStyle }}
                    variants={wordVariants}
                  >
                    {word}
                  </motion.span>
                </span>
              ))}
            </React.Fragment>
          ))}
        </motion.div>
      </div>
    );
  }
  // Original slide-up animation
  return (
    <div className="custom-split-title overflow-hidden" style={{ zIndex: 9, margin: alignCenter ? '0 auto' : '0' }}>
      <motion.div
        initial={{ y: "100%", opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        className={cx(styles.splitTitle, headingClass, additionalClass, titleProps.className)}
        style={{ 
          textTransform: uppercase ? 'uppercase' : 'none',
          ...(textAlign && { textAlign })
        }}
      >
        <span className={cx(styles.partA, partAClassName)} style={partAStyle}>
          {parseTextWithBreaks(partA, removeBreaksOnDesktop)}
        </span>
        <span className={cx(styles.partB, partBClassName)} style={partBStyle}>
          {parseTextWithBreaks(partB, removeBreaksOnDesktop)}
        </span>
        {partC && (
          <span className={cx(styles.partC, partCClassName)} style={partCStyle}>
            {parseTextWithBreaks(partC, removeBreaksOnDesktop)}
          </span>
        )}
      </motion.div>
    </div>
  );
};
